/**
 * Consent Popup Service
 * Centralized service to control the consent popup visibility
 * Also tracks if consent is currently being saved to prevent race conditions
 */

import { Injectable, signal } from '@angular/core';
import { Subject } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class ConsentPopupService {
  // Signal to control popup visibility
  private _shouldShow = signal(false);

  // Signal to track if consent save is in progress (prevents 401 clearing data)
  private _isSavingConsent = signal(false);

  // Signal to track if consent has been accepted (for shared links waiting)
  private _hasConsent = signal(false);

  // Subject to emit when consent is accepted (for components waiting on shared links)
  private _consentAccepted$ = new Subject<void>();

  // Public read-only signals
  shouldShow = this._shouldShow.asReadonly();
  isSavingConsent = this._isSavingConsent.asReadonly();
  hasConsent = this._hasConsent.asReadonly();

  // Observable for consent acceptance events
  consentAccepted$ = this._consentAccepted$.asObservable();

  /**
   * Request to show the consent popup
   */
  requestConsent(): void {
    // Don't request if we're currently saving
    if (this._isSavingConsent()) {
      console.log('🍪 Skipping consent request - save in progress');
      return;
    }
    console.log('🍪 ConsentPopupService.requestConsent() called');
    console.log('🍪 Current shouldShow value:', this._shouldShow());
    this._shouldShow.set(true);
    console.log('🍪 Updated shouldShow value:', this._shouldShow());
  }

  /**
   * Hide the consent popup and mark consent as accepted
   */
  hideConsent(): void {
    this._shouldShow.set(false);
    this._hasConsent.set(true);
    // Emit event for components waiting on consent
    this._consentAccepted$.next();
    console.log('🍪 Consent accepted - notifying waiting components');
  }

  /**
   * Set consent status (called when auth token is found)
   * If consent is granted, emit event for waiting components
   */
  setConsentStatus(hasConsent: boolean): void {
    const wasConsentGranted = !this._hasConsent() && hasConsent;
    this._hasConsent.set(hasConsent);

    // If consent was just granted (changed from false to true), notify waiting components
    if (wasConsentGranted) {
      console.log(
        '🍪 Consent status set to true - notifying waiting components',
      );
      this._consentAccepted$.next();
    }
  }

  /**
   * Mark consent save as started
   */
  startSaving(): void {
    console.log(
      '🍪 ConsentPopupService.startSaving() - marking save in progress',
    );
    this._isSavingConsent.set(true);
  }

  /**
   * Mark consent save as completed
   */
  finishSaving(): void {
    console.log('🍪 ConsentPopupService.finishSaving() - save completed');
    this._isSavingConsent.set(false);
  }
}
